import type { Directive } from 'vue'

export type TTooltipDelay = 'fast' | 'medium' | 'slow'
export type TTooltipValue = string | { text: string; delay?: TTooltipDelay }

const DELAYS: Record<TTooltipDelay, number> = {
  fast: 50,
  medium: 150,
  slow: 300,
}

let tooltipEl: HTMLDivElement | null = null

function ensureTooltip(): HTMLDivElement {
  if (!tooltipEl) {
    tooltipEl = document.createElement('div')
    tooltipEl.setAttribute('role', 'tooltip')
    tooltipEl.style.cssText =
      'position:fixed;z-index:9999;pointer-events:none;white-space:pre-line;' +
      'border-radius:4px;background:#111827;color:#fff;font-size:0.75rem;' +
      'line-height:1.25rem;padding:4px 8px;opacity:0;transition:opacity 150ms'
    document.body.appendChild(tooltipEl)
  }
  return tooltipEl
}

function show(el: HTMLElement, text: string) {
  const tooltip = ensureTooltip()
  tooltip.textContent = text
  const rect = el.getBoundingClientRect()
  const tipRect = tooltip.getBoundingClientRect()
  const left = Math.max(
    8,
    Math.min(
      rect.left + rect.width / 2 - tipRect.width / 2,
      window.innerWidth - tipRect.width - 8,
    ),
  )
  tooltip.style.left = `${left}px`
  tooltip.style.top = `${rect.bottom + 6}px`
  tooltip.style.opacity = '1'
}

function hide() {
  if (tooltipEl) tooltipEl.style.opacity = '0'
}

function parseValue(value: TTooltipValue): { text: string; delay: number } {
  if (typeof value === 'string') return { text: value, delay: DELAYS.fast }
  return { text: value.text, delay: DELAYS[value.delay ?? 'fast'] }
}

interface ITooltipHandlers {
  enter: () => void
  leave: () => void
  ctx: { text: string; delay: number }
  timer: ReturnType<typeof setTimeout> | null
}

const handlers = new WeakMap<HTMLElement, ITooltipHandlers>()

export const vTooltip: Directive<HTMLElement, TTooltipValue> = {
  mounted(el, binding) {
    const { text, delay } = parseValue(binding.value)
    const ctx = { text, delay }
    const handler: ITooltipHandlers = { enter: () => {}, leave: () => {}, ctx, timer: null }

    handler.enter = () => {
      if (!ctx.text) return
      handler.timer = setTimeout(() => show(el, ctx.text), ctx.delay)
    }
    handler.leave = () => {
      if (handler.timer !== null) {
        clearTimeout(handler.timer)
        handler.timer = null
      }
      hide()
    }

    handlers.set(el, handler)
    el.addEventListener('mouseenter', handler.enter)
    el.addEventListener('mouseleave', handler.leave)
    el.addEventListener('focusin', handler.enter)
    el.addEventListener('focusout', handler.leave)
  },

  updated(el, binding) {
    const handler = handlers.get(el)
    if (handler) {
      const { text, delay } = parseValue(binding.value)
      handler.ctx.text = text
      handler.ctx.delay = delay
    }
  },

  unmounted(el) {
    const handler = handlers.get(el)
    if (handler) {
      if (handler.timer !== null) clearTimeout(handler.timer)
      el.removeEventListener('mouseenter', handler.enter)
      el.removeEventListener('mouseleave', handler.leave)
      el.removeEventListener('focusin', handler.enter)
      el.removeEventListener('focusout', handler.leave)
      handlers.delete(el)
    }
  },
}
